import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Clock, TrendingUp } from 'lucide-react'
import { CATEGORIES, CATEGORY_GROUPS } from '@/utils/categories'

interface SearchSuggestionsProps {
  query: string
  isOpen: boolean
  onClose: () => void
  onSelect: (value: string) => void
}

const RECENT_SEARCHES_KEY = 'isqroll_recent_searches'
const MAX_RECENT = 5
const MAX_SUGGESTIONS = 6

// Popular search terms for NZ marketplace
const POPULAR_SEARCHES = [
  'Toyota', 'Ford Ranger', 'iPhone', 'PlayStation',
  'House for rent', 'Laptop', 'Furniture', 'Motorbike',
]

function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export function saveRecentSearch(query: string) {
  if (!query.trim()) return
  try {
    const recent = getRecentSearches().filter((s) => s !== query.trim())
    recent.unshift(query.trim())
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
  } catch {
    // localStorage unavailable
  }
}

export function SearchSuggestions({ query, isOpen, onClose, onSelect }: SearchSuggestionsProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const trimmed = query.trim().toLowerCase()

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen, onClose])

  // Reset selection when query changes
  useEffect(() => { setSelectedIndex(-1) }, [query])

  // Build suggestions
  const suggestions: { type: 'recent' | 'category' | 'popular'; label: string; value: string; icon?: string }[] = []

  if (!trimmed) {
    // Show recent searches + popular
    const recent = getRecentSearches()
    recent.forEach((s) => suggestions.push({ type: 'recent', label: s, value: s }))
    POPULAR_SEARCHES.slice(0, MAX_SUGGESTIONS - recent.length).forEach((s) =>
      suggestions.push({ type: 'popular', label: s, value: s })
    )
  } else {
    // Match categories
    const matchingCategories = CATEGORIES.filter((c) =>
      c.name.toLowerCase().includes(trimmed)
    ).slice(0, 3)
    matchingCategories.forEach((c) =>
      suggestions.push({ type: 'category', label: c.name, value: c.name })
    )

    // Match category groups
    const matchingGroups = CATEGORY_GROUPS.filter((g) =>
      g.name.toLowerCase().includes(trimmed)
    ).slice(0, 2)
    matchingGroups.forEach((g) =>
      suggestions.push({ type: 'category', label: g.name, value: g.name, icon: g.icon })
    )

    // Match popular searches
    POPULAR_SEARCHES.filter((s) => s.toLowerCase().includes(trimmed))
      .slice(0, MAX_SUGGESTIONS - suggestions.length)
      .forEach((s) => suggestions.push({ type: 'popular', label: s, value: s }))
  }

  const handleSelect = useCallback((value: string) => {
    saveRecentSearch(value)
    onSelect(value)
    onClose()
  }, [onSelect, onClose])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      handleSelect(suggestions[selectedIndex].value)
    } else if (e.key === 'Escape') {
      onClose()
    }
  }, [isOpen, suggestions, selectedIndex, handleSelect, onClose])

  if (!isOpen || suggestions.length === 0) return null

  return (
    <div
      ref={ref}
      className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg z-50 overflow-hidden"
      role="listbox"
      aria-label="Search suggestions"
      onKeyDown={handleKeyDown}
    >
      {suggestions.map((item, index) => (
        <button
          key={`${item.type}-${item.value}`}
          role="option"
          aria-selected={index === selectedIndex}
          onClick={() => handleSelect(item.value)}
          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors ${
            index === selectedIndex ? 'bg-slate-50' : ''
          }`}
        >
          {item.type === 'recent' ? (
            <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
          ) : item.type === 'category' ? (
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          ) : item.type === 'popular' ? (
            <TrendingUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
          ) : (
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          )}
          <span className="truncate">{item.label}</span>
          {item.type === 'category' && (
            <span className="ml-auto text-xs text-slate-400">Category</span>
          )}
        </button>
      ))}
    </div>
  )
}
